import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Lock, MessageSquarePlus } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import DatePicker from "@/components/DatePicker";
import { api, toast } from "../../App";

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const EMPTY = (contactId = "") => ({ date: todayISO(), remarks: "", next_followup_date: "", contact_method: "", spoken_to_contact_id: contactId });

const PickSelect = ({ label, value, onChange, options, placeholder, testId }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label} *</Label>
    <Select key={value || "empty"} value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className="bg-white" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const NoteForm = ({ estimationId, contacts, methods, defaultContactId, onSaved }) => {
  const initialContact = contacts.some((c) => c.id === defaultContactId) ? defaultContactId : "";
  const [form, setForm] = useState(EMPTY(initialContact));
  const [saving, setSaving] = useState(false);
  if (!contacts.length) {
    return (
      <div className="p-3 rounded-md3-md bg-amber-50 text-sm text-amber-900" data-testid="followup-no-contacts">
        This client has no contacts yet. Add a contact under the client first, then record the follow-up.
      </div>
    );
  }
  const save = async () => {
    if (!form.date) return toast.error("Date is required");
    if (!form.contact_method) return toast.error("Contact method is required");
    if (!form.spoken_to_contact_id) return toast.error("Spoken To is required");
    if (!form.remarks.trim()) return toast.error("Remarks are required");
    try {
      setSaving(true);
      await api.post(`/estimations/${estimationId}/followup-notes`, { ...form, next_followup_date: form.next_followup_date || null });
      toast.success(form.next_followup_date ? "Note added — follow-up scheduled in My Workspace" : "Follow-up note added");
      setForm(EMPTY(initialContact));
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add note");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-3 p-4 rounded-md3-lg bg-md3-surface-container" data-testid="followup-note-form">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Date *</Label>
          <DatePicker value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="followup-note-date" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Next follow-up (optional)</Label>
          <DatePicker value={form.next_followup_date} min={form.date} onChange={(e) => setForm({ ...form, next_followup_date: e.target.value })} data-testid="followup-note-next-date" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <PickSelect label="Contact Method" value={form.contact_method} onChange={(v) => setForm({ ...form, contact_method: v })} options={methods} placeholder="Email / Phone / Meeting" testId="followup-note-method" />
        <PickSelect label="Spoken To" value={form.spoken_to_contact_id} onChange={(v) => setForm({ ...form, spoken_to_contact_id: v })} options={contacts.map((c) => ({ value: c.id, label: c.designation ? `${c.name} — ${c.designation}` : c.name }))} placeholder="Select client contact" testId="followup-note-spoken-to" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Remarks *</Label>
        <Textarea rows={3} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="What was discussed, next steps..." data-testid="followup-note-remarks" />
      </div>
      <Button onClick={save} disabled={saving} className="w-full rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary" data-testid="followup-note-save">
        <MessageSquarePlus className="h-4 w-4 mr-1.5" /> {saving ? "Saving..." : "Add Follow-up Note"}
      </Button>
    </div>
  );
};

const NoteItem = ({ n }) => (
  <div className="relative pl-6 pb-4 border-l-2 border-md3-outline-variant last:border-transparent" data-testid={`followup-note-${n.note_number}`}>
    <span className="absolute -left-[11px] top-0 h-5 w-5 rounded-full bg-md3-primary text-md3-on-primary text-[10px] font-bold flex items-center justify-center">{n.note_number}</span>
    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
      <span className="font-semibold text-gray-900">{n.date}</span>
      <span>· {n.created_by_name}</span>
      {n.version && <span>· v{n.version}</span>}
    </div>
    {(n.contact_method || n.spoken_to_name) && (
      <p className="text-xs text-gray-700 mt-1" data-testid={`followup-note-contact-${n.note_number}`}>
        <span className="inline-block px-1.5 py-0.5 rounded bg-md3-surface-container font-medium capitalize mr-1.5">{n.contact_method || "—"}</span>
        {n.spoken_to_name && <>Spoke to <strong>{n.spoken_to_name}</strong></>}
      </p>
    )}
    <p className="text-sm mt-1 whitespace-pre-wrap">{n.remarks}</p>
    {n.next_followup_date && <p className="text-xs text-indigo-700 mt-1">Next follow-up: {n.next_followup_date}</p>}
  </div>
);

const BLANK = { notes: [], locked_reason: null, contacts: [], contact_methods: [], default_contact_id: null, loaded: false };

export const FollowUpNotesSheet = ({ estimation, onClose, onChanged }) => {
  const [data, setData] = useState(BLANK);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/estimations/${estimation.id}/followup-notes`);
      setData({ ...res.data, loaded: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load follow-up notes");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    setData(BLANK);
    if (estimation) load();
  }, [estimation?.id]);

  return (
    <Sheet open={!!estimation} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg bg-white overflow-y-auto" data-testid="followup-notes-sheet">
        <SheetHeader>
          <SheetTitle>Follow-up Notes · {estimation?.estimation_number}</SheetTitle>
          <p className="text-sm text-gray-600">{estimation?.client_name} — {estimation?.project_name}</p>
        </SheetHeader>
        <div className="mt-5 space-y-5">
          {data.locked_reason ? (
            <div className="flex items-start gap-2 p-3 rounded-md3-md bg-gray-100 text-sm text-gray-700" data-testid="followup-notes-locked">
              <Lock className="h-4 w-4 mt-0.5 flex-shrink-0" /> {data.locked_reason}
            </div>
          ) : estimation && data.loaded && (
            <NoteForm
              key={estimation.id}
              estimationId={estimation.id}
              contacts={data.contacts}
              methods={data.contact_methods}
              defaultContactId={data.default_contact_id}
              onSaved={() => { load(); onChanged(); }}
            />
          )}
          <div>
            <h4 className="text-sm font-semibold mb-3">History ({data.notes.length})</h4>
            {loading ? <p className="text-sm text-gray-500">Loading...</p> : data.notes.length === 0 ? (
              <p className="text-sm text-gray-500" data-testid="followup-notes-empty">No follow-up notes yet.</p>
            ) : (
              <div className="ml-2.5">{data.notes.map((n) => <NoteItem key={n.id} n={n} />)}</div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
